import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const ROTATE_MS = 5500;

/**
 * Auto-rotierender Hero-Bildslider (Crossfade).
 * Slide 0 wird eager geladen (LCP), der Rest lazy. Pausiert bei Hover/Fokus
 * und bei prefers-reduced-motion. SSR liefert Slide 0 statisch (inject.js).
 * Controls sitzen unten links (Ursprungsposition), bottomBar (Badges/
 * Compliance) eine Zeile darüber rechts.
 */
export default function HeroSlider({ slides, bottomBar = null }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const count = slides.length;

  const go = useCallback((i) => setActive((i + count) % count), [count]);

  useEffect(() => {
    if (paused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    timer.current = setInterval(() => setActive((a) => (a + 1) % count), ROTATE_MS);
    return () => clearInterval(timer.current);
  }, [paused, count]);

  return (
    <div
      className="absolute inset-0"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      data-testid="hero-slider-container"
    >
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className="absolute inset-0 transition-opacity duration-700 ease-in-out"
          style={{ opacity: i === active ? 1 : 0 }}
          aria-hidden={i !== active}
          data-testid={`hero-slider-slide-${i}`}
        >
          <img
            src={slide.image}
            alt={i === active ? slide.alt : ""}
            width="1920"
            height="1080"
            loading={i === 0 ? "eager" : "lazy"}
            fetchPriority={i === 0 ? "high" : undefined}
            className="h-full w-full object-cover"
          />
        </div>
      ))}
      {/* Auftraggeber-Wunsch 09.09.2026: hellerer Verlauf (ca. 50 % heller links) */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0E0E10]/80 via-[#0E0E10]/40 to-[#0E0E10]/5" aria-hidden="true" />
      {/* Badges/Compliance-Zeile: hochgeschoben, rechts verteilt */}
      {bottomBar && (
        <div className="absolute inset-x-0 bottom-24 z-10">
          <div
            className="mx-auto flex max-w-7xl flex-wrap items-center justify-start gap-x-5 gap-y-2 px-4 text-[11px] leading-snug text-[#F5F5F3]/90 sm:px-6 sm:text-sm lg:px-12"
            data-testid="hero-bottom-bar"
          >
            {bottomBar}
          </div>
        </div>
      )}
      {/* Slider-Controls: Ursprungsposition unten links */}
      <div className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 sm:left-6 sm:translate-x-0 lg:left-12" data-testid="hero-slider-controls">
        <button
          type="button"
          onClick={() => go(active - 1)}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-[#F5F5F3]/20 bg-[#0E0E10]/60 text-[#F5F5F3] backdrop-blur transition-colors hover:border-[#E11D2A]/60 hover:bg-[#0E0E10]/80"
          aria-label="Vorheriges Bild"
          data-testid="hero-slider-prev"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => go(i)}
            className={`h-2.5 rounded-full transition-all duration-300 ${i === active ? "w-8 bg-[#E11D2A]" : "w-2.5 bg-[#F5F5F3]/30 hover:bg-[#F5F5F3]/60"}`}
            aria-label={`Bild ${i + 1}: ${slide.tag}`}
            aria-current={i === active}
            data-testid={`hero-slider-dot-${i}`}
          />
        ))}
        <button
          type="button"
          onClick={() => go(active + 1)}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-[#F5F5F3]/20 bg-[#0E0E10]/60 text-[#F5F5F3] backdrop-blur transition-colors hover:border-[#E11D2A]/60 hover:bg-[#0E0E10]/80"
          aria-label="Nächstes Bild"
          data-testid="hero-slider-next"
        >
          <ChevronRight className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      <div className="absolute bottom-6 right-6 z-10 hidden sm:block" aria-hidden="true">
        <span className="rounded-full bg-[#0E0E10]/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#F5F5F3] backdrop-blur">
          {slides[active].tag}
        </span>
      </div>
      <span className="sr-only" aria-live="polite">
        {`Bild ${active + 1} von ${count}: ${slides[active].alt}`}
      </span>
    </div>
  );
}
